import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Loader2, Search, Undo2, CheckSquare, Square, ArrowLeft } from "lucide-react";
import api from "../../lib/api";
import toast from "react-hot-toast";
import Pagination from "../ui/pagination";

interface B2CEntityProduct {
  id: string;
  hsn: string;
  mrp: string;
  pack: string;
  name: string;
  mfr: string;
  gstp: number;
  branch_id: number;
  tp_code: string | null;
  med_products_id: number | null;
  code: string;
  no_match_found_at?: string;
  no_match_found_by_name?: string;
  no_match_found_by_email?: string;
}

interface NoMatchFoundB2CProductsProps {
  entities: any;
  selectedEntity?: any;
  onEntityChange?: (entity: any) => void;
  page?: number;
  onPageChange?: (page: number) => void;
  showOnlyOtc?: boolean;
  showMrpZero?: boolean;
  onStatisticsUpdate?: () => void;
}

const NoMatchFoundB2CProducts = ({
  entities,
  selectedEntity: parentSelectedEntity,
  onEntityChange,
  page: parentPage,
  onPageChange,
  showOnlyOtc,
  showMrpZero,
  onStatisticsUpdate,
}: NoMatchFoundB2CProductsProps) => {
  const [products, setProducts] = useState<B2CEntityProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(parentPage || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedEntity, setSelectedEntity] = useState<any>(
    parentSelectedEntity || null
  );

  // Search and bulk action states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const fetchProducts = useCallback(async () => {
    // Always fetch products - either for specific entity or all entities
    setLoading(true);
    try {
      const response = await api.getNoMatchFoundEntityProducts(
        selectedEntity?.value || null,
        page,
        20,
        showOnlyOtc || false,
        searchQuery,
        showMrpZero || false
      );

      if (response?.success) {
        setProducts(response.data.products || []);
        setTotalPages(response.data.totalPages || 1);
      } else {
        setProducts([]);
        setTotalPages(1);
      }
    } catch (error) {
      toast.error("Failed to load no match found products");
      setProducts([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [selectedEntity?.value, page, showOnlyOtc, searchQuery, showMrpZero]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    setSelectedEntity(parentSelectedEntity);
  }, [parentSelectedEntity]);

  useEffect(() => {
    if (parentPage !== page) {
      setPage(parentPage || 1);
    }
  }, [parentPage]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchProducts();
  };

  const handleSelectProduct = (productId: string) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map(p => p.id)));
    }
  };

  const handleMoveBackToUnmapped = async (productIds?: string[]) => {
    const idsToMove = productIds || Array.from(selectedProducts);

    if (idsToMove.length === 0) {
      toast.error("Please select products to move back");
      return;
    }

    setBulkActionLoading(true);
    try {
      const response = await api.moveProductsBackToUnmapped(idsToMove);

      if (response?.success) {
        toast.success(`${idsToMove.length} products moved back to unmapped`);
        setSelectedProducts(new Set());
        fetchProducts();
        // Refresh statistics to update counts now that the DB transaction has completed
        if (onStatisticsUpdate) {
          onStatisticsUpdate();
        }
      } else {
        toast.error(response?.error || "Failed to move products");
      }
    } catch (error) {
      toast.error("Failed to move products back to unmapped");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "Unknown";
    return new Date(dateString).toLocaleDateString() + " " + new Date(dateString).toLocaleTimeString();
  };

  // Remove the early return - we want to show all products when no entity is selected

  return (
    <div className="space-y-4">
      {/* Header with Search and Bulk Actions */}
      <div className="bg-gray-800 px-6 py-4 border-b border-gray-700">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-md">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search by name, code, or manufacturer..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-gray-700 border-gray-600 text-white placeholder-gray-400"
              />
            </div>
            <Button type="submit" variant="outline" size="sm" className="border-gray-600 text-gray-300 hover:bg-gray-700">
              Search
            </Button>
          </form>

          {/* Bulk Actions */}
          {selectedProducts.size > 0 && (
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="bg-blue-600 text-white">
                {selectedProducts.size} selected
              </Badge>
              <Button
                onClick={() => handleMoveBackToUnmapped()}
                disabled={bulkActionLoading}
                size="sm"
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                {bulkActionLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <ArrowLeft className="w-4 h-4 mr-2" />
                )}
                Move Back to Unmapped
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-400 mr-2" />
            <span className="text-gray-300">Loading no match found products...</span>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center p-8 text-gray-500">
            <CheckSquare className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium mb-2">No products marked as "No Match Found"</h3>
            <p className="text-sm">Products that cannot be matched will appear here.</p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="border-gray-700 hover:bg-gray-700/50">
                  <TableHead className="text-gray-300 w-12">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleSelectAll}
                      className="h-6 w-6 p-0"
                    >
                      {selectedProducts.size === products.length ? (
                        <CheckSquare className="w-4 h-4" />
                      ) : (
                        <Square className="w-4 h-4" />
                      )}
                    </Button>
                  </TableHead>
                  <TableHead className="text-gray-300">PRODUCT CODE</TableHead>
                  <TableHead className="text-gray-300">PRODUCT NAME</TableHead>
                  <TableHead className="text-gray-300">MRP</TableHead>
                  <TableHead className="text-gray-300">PACKING</TableHead>
                  <TableHead className="text-gray-300">MANUFACTURER</TableHead>
                  <TableHead className="text-gray-300">MARKED BY</TableHead>
                  <TableHead className="text-gray-300">MARKED AT</TableHead>
                  <TableHead className="text-gray-300">ACTIONS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product) => (
                  <TableRow
                    key={product.id}
                    className="border-gray-700 hover:bg-gray-700/50"
                  >
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSelectProduct(product.id)}
                        className="h-6 w-6 p-0"
                      >
                        {selectedProducts.has(product.id) ? (
                          <CheckSquare className="w-4 h-4 text-blue-400" />
                        ) : (
                          <Square className="w-4 h-4 text-gray-400" />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-gray-300">
                      {product.code}
                    </TableCell>
                    <TableCell className="text-white max-w-xs">
                      <div className="truncate" title={product.name}>
                        {product.name}
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-300">
                      ₹{product.mrp}
                    </TableCell>
                    <TableCell className="text-gray-300">
                      {product.pack}
                    </TableCell>
                    <TableCell className="text-gray-300 max-w-xs">
                      <div className="truncate" title={product.mfr}>
                        {product.mfr}
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-300">
                      <div className="text-sm">
                        {product.no_match_found_by_name || 'Unknown'}
                      </div>
                      {product.no_match_found_by_email && (
                        <div className="text-xs text-gray-500">
                          {product.no_match_found_by_email}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-300 text-sm">
                      {formatDate(product.no_match_found_at || '')}
                    </TableCell>
                    <TableCell>
                      <Button
                        onClick={() => handleMoveBackToUnmapped([product.id])}
                        disabled={bulkActionLoading}
                        size="sm"
                        variant="outline"
                        className="border-orange-600 text-orange-400 hover:bg-orange-600 hover:text-white px-2 py-1 text-xs h-7 flex items-center gap-1"
                      >
                        <Undo2 className="w-3 h-3" />
                        Move Back
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="border-t border-gray-700 p-4">
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NoMatchFoundB2CProducts;